/**
 * Vault Picker Module
 * Modal for selecting documents (for linking or other purposes)
 */

import * as State from './state.js';
import { escapeAttr } from '../../../../engine/core/validation.js';
import { t } from '../../../../shared/i18n/index.js';
import { roleLabel } from './list.js';

// ============================================
// Picker State
// ============================================

let _pickerModal = null;
let _pickerResolve = null;
let _pickerReject = null;
let _pickerAbortController = null;

// ============================================
// Dependencies (injected via init)
// ============================================

let _deps = {
  escapeHtml: null,
};

/**
 * Initialize picker module with dependencies
 * @param {Object} deps - Dependencies
 * @param {Function} deps.escapeHtml - HTML escape function
 */
export function initPicker(deps) {
  if (deps.escapeHtml) _deps.escapeHtml = deps.escapeHtml;
}

// ============================================
// Picker Modal
// ============================================

/**
 * Open a picker modal to select a document.
 * Shows owned documents and shared documents the user has access to.
 *
 * @param {Object} options
 * @param {string} options.title - Modal title
 * @param {string} options.mode - "all" (default) shows all docs, "shared-only" shows only shared docs
 * @param {Function} options.filter - Optional filter function (item) => boolean
 * @param {string} options.excludeDocId - Optional doc ID to exclude from results
 * @param {Iterable<string>} options.excludeDocIds - Optional set/array of doc IDs to exclude
 * @returns {Promise<{docId: string, title: string, type: string}|null>}
 */
export function openPicker(options = {}) {
  return new Promise((resolve, reject) => {
    _pickerResolve = resolve;
    _pickerReject = reject;

    const VaultStorage = State.getVaultStorage();
    const title = options.title || t('vault.picker.title');
    const filter = options.filter || (() => true);
    const mode = options.mode || "all";
    const excludeDocId = options.excludeDocId || null;
    const excludeDocIds = new Set(options.excludeDocIds || []);
    if (excludeDocId) excludeDocIds.add(excludeDocId);

    // Build list of available documents
    const allDocs = [];

    // Add owned documents
    if (mode !== "shared-only") {
      const index = VaultStorage.getIndex();
      const ownedDocs = index?.docs?.length > 0
        ? index.docs.filter(d => !d.role || d.role === "owner")
        : (index?.docs || []).filter(d => !d.role || d.role === "owner");
      if (ownedDocs.length > 0) {
        for (const map of ownedDocs) {
          if (excludeDocIds.has(map.docId)) continue;
          allDocs.push({
            id: map.docId,
            title: map.title || t('common.untitled'),
            type: "owned",
            icon: "📄",
            meta: "",
          });
        }
      }
    }

    // Add shared documents (received shares — docs where user is not the owner)
    const index = VaultStorage.getIndex();
    const sharedDocs = (index?.docs || []).filter(d => d.role !== "owner");
    for (const s of sharedDocs) {
      if (excludeDocIds.has(s.docId)) continue;
      if (!filter(s)) continue;
      // For linking, only show shares where user has edit access
      if (mode === "link" && s.role === "viewer") continue;
      allDocs.push({
        id: s.docId,
        title: s.title || t('common.untitled'),
        type: "shared",
        icon: "🔗",
        meta: t('vault.share.from', { name: s.ownerUsername || t('vault.share.unknownUser') }),
        role: s.role,
      });
    }

    if (allDocs.length === 0) {
      resolve(null);
      return;
    }

    // Create AbortController for cleanup
    _pickerAbortController = new AbortController();
    const signal = _pickerAbortController.signal;

    function renderDocItems(docs) {
      if (docs.length === 0) {
        return `<div class="vault-picker-empty">${_deps.escapeHtml(t('vault.picker.noMatches'))}</div>`;
      }
      return docs
        .map(
          (doc) => `
          <div class="vault-picker-item" data-doc-id="${escapeAttr(doc.id)}" data-doc-type="${escapeAttr(doc.type)}">
            <span class="vault-picker-icon">${doc.icon}</span>
            <div class="vault-picker-info">
              <div class="vault-picker-name">${_deps.escapeHtml(doc.title)}</div>
              ${doc.meta ? `<div class="vault-picker-meta">
                ${_deps.escapeHtml(doc.meta)}
                ${doc.role ? `<span class="vault-role-badge vault-role-${escapeAttr(doc.role)}">${_deps.escapeHtml(roleLabel(doc.role))}</span>` : ""}
              </div>` : ""}
            </div>
          </div>
        `,
        )
        .join("");
    }

    function filterDocs(query) {
      if (!query) return allDocs;
      const q = query.toLowerCase();
      return allDocs.filter((d) => {
        const titleMatch = d.title.toLowerCase().includes(q);
        const metaMatch = (d.meta || "").toLowerCase().includes(q);
        return titleMatch || metaMatch;
      });
    }

    function attachItemHandlers() {
      _pickerModal.querySelectorAll(".vault-picker-item").forEach((item) => {
        item.addEventListener("click", () => {
          const docId = item.dataset.docId;
          const docType = item.dataset.docType;
          const doc = allDocs.find((d) => d.id === docId);
          closePickerWithResult({
            docId,
            title: doc?.title || t('common.untitled'),
            type: docType,
            // Legacy compat: also include shareId/share for old callers
            shareId: docId,
            share: doc,
          });
        }, { signal });
      });
    }

    _pickerModal = document.createElement("div");
    _pickerModal.className = "vault-picker-overlay";
    _pickerModal.innerHTML = `
      <div class="vault-picker-modal">
        <div class="vault-picker-header">
          <h3>${_deps.escapeHtml(title)}</h3>
          <button class="vault-picker-close" title="${escapeAttr(t('common.cancel'))}">✕</button>
        </div>
        <div class="vault-picker-search">
          <svg class="vault-picker-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input type="text" class="vault-picker-search-input" placeholder="${escapeAttr(t('vault.picker.filter'))}" />
        </div>
        <div class="vault-picker-content">
          ${renderDocItems(allDocs)}
        </div>
        <div class="vault-picker-footer">
          <button class="vault-picker-btn vault-picker-cancel">${_deps.escapeHtml(t('common.cancel'))}</button>
        </div>
      </div>
    `;

    document.body.appendChild(_pickerModal);

    _pickerModal
      .querySelector(".vault-picker-close")
      .addEventListener("click", closePicker, { signal });
    _pickerModal
      .querySelector(".vault-picker-cancel")
      .addEventListener("click", closePicker, { signal });
    _pickerModal.addEventListener("click", (e) => {
      if (e.target === _pickerModal) closePicker();
    }, { signal });

    const searchInput = _pickerModal.querySelector(
      ".vault-picker-search-input",
    );
    searchInput.addEventListener("input", (e) => {
      const query = e.target.value.trim();
      const filtered = filterDocs(query);
      const content = _pickerModal.querySelector(".vault-picker-content");
      content.innerHTML = renderDocItems(filtered);
      attachItemHandlers();
    }, { signal });

    setTimeout(() => searchInput.focus(), 50);

    attachItemHandlers();

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        closePicker();
      }
    }, { signal });
  });
}

/**
 * Close the picker modal without selecting
 */
export function closePicker() {
  if (_pickerAbortController) {
    _pickerAbortController.abort();
    _pickerAbortController = null;
  }
  if (_pickerModal) {
    _pickerModal.remove();
    _pickerModal = null;
  }
  if (_pickerResolve) {
    _pickerResolve(null);
    _pickerResolve = null;
    _pickerReject = null;
  }
}

/**
 * Close the picker modal with a result
 * @param {Object} result - The selected result
 */
function closePickerWithResult(result) {
  if (_pickerAbortController) {
    _pickerAbortController.abort();
    _pickerAbortController = null;
  }
  if (_pickerModal) {
    _pickerModal.remove();
    _pickerModal = null;
  }
  if (_pickerResolve) {
    _pickerResolve(result);
    _pickerResolve = null;
    _pickerReject = null;
  }
}
